import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AiThread, AiMessage } from '@lognai/plugin-ai-core';
import { ChatMessage } from './ChatMessage';
import { TurnError, looksLikeRawError } from './TurnError';
import { ChatComposer } from './ChatComposer';
import { ThreadList } from './ThreadList';
import { useChatStream, type DraftRef } from '../../hooks/use-chat-stream';
import { useAiThreads, useCreateAiThread, useUpdateAiThread } from '../../hooks/use-threads';
import { useThreadMessages } from '../../hooks/use-thread-messages';
import { listAiMessages } from '../../api/messages';
import { useAiBundlesForThread } from '../../hooks/use-bundle';
import { useQueryClient } from '@tanstack/react-query';
import { aiQueryKeys } from '../../hooks/query-keys';
import { DraftBundleCard } from '../DraftBundleCard';
import { BundleReviewPanel } from '../BundleReviewPanel';
import { UndoBanner } from '../UndoBanner';
import { KillSwitchBanner } from '../KillSwitchBanner';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import { useEntityLabel } from '../../hooks/use-entity-label';

export interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
  pageContext?: {
    pluginId: string;
    entityType: string;
    entityId: string;
    url: string;
  };
  /** If supplied, the sidebar auto-submits this as the first user message.
   *  Used by the Command Palette's AI fallback to carry the typed query
   *  into the conversation. */
  initialPrompt?: string;
  /** If supplied, the sidebar opens directly into this thread. */
  initialThreadId?: string;
}

interface LocalMessage {
  role: 'user' | 'assistant';
  content: string;
  toolTrace?: Array<{ name: string; uiLabel?: string; done: boolean }>;
  draftRefs?: DraftRef[];
  messageId?: string;
  /**
   * The reader stopped this turn part-way. It carries no `messageId` because
   * the server never persisted it, so it is gone on the next reload — which the
   * bubble says, rather than letting a half-answer look like a whole one.
   */
  stopped?: boolean;
}

// Convert a persisted AiMessage from the API into the sidebar's LocalMessage
// shape. Tool calls become a "done" trace; tool/system rows are dropped from
// the rendered transcript (they're context for the model, not user-facing).
const toLocalMessage = (m: AiMessage): LocalMessage | null => {
  if (m.role !== 'user' && m.role !== 'assistant') return null;
  return {
    role: m.role,
    content: m.content,
    toolTrace: (m.toolCalls ?? []).map((tc) => ({
      name: tc.name,
      uiLabel: undefined,
      done: true,
    })),
    draftRefs: (m.draftRefs ?? []) as DraftRef[],
    messageId: m.id,
  };
};

/**
 * Where the last-opened conversation is remembered, per browser.
 *
 * Namespaced rather than a bare "thread", because this is shared storage with
 * every other page the app ever runs.
 */
const LAST_THREAD_KEY = 'lognai.ai.lastThreadId';

const humanize = (raw: string): string => {
  if (!raw) return raw;
  const spaced = raw.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced[0]!.toUpperCase() + spaced.slice(1);
};

// Derive a short, human-readable title from the user's first message.
const deriveTitle = (text: string): string => {
  const first = text.trim().split('\n')[0]!.trim();
  if (first.length <= 60) return first;
  const truncated = first.slice(0, 60).replace(/\s\S*$/, '');
  return truncated || first.slice(0, 60);
};

// Pre-token placeholder: shows what the agent is actually doing right now.
// The label is derived from the in-flight tool (or "Thinking" if none yet) —
// never a filler string disconnected from the backend.
const derivePendingLabel = (
  toolEvents: Array<{ name: string; uiLabel?: string; done: boolean }>
): string => {
  const inFlight = toolEvents.filter((t) => !t.done);
  if (inFlight.length === 0) return 'Thinking';
  const t = inFlight[inFlight.length - 1]!;
  if (t.uiLabel) return t.uiLabel;
  const after = t.name.includes('.') ? t.name.slice(t.name.indexOf('.') + 1) : t.name;
  const spaced = after.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.length ? spaced[0]!.toUpperCase() + spaced.slice(1) : t.name;
};

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ open, onClose, pageContext, initialPrompt, initialThreadId }) => {
  const [thread, setThread] = useState<AiThread | null>(null);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  /** The failed turn, if the last one failed — separate from stream.error so a
   *  raw error payload returned AS the answer is treated as a failure too. */
  const [turnError, setTurnError] = useState<string | null>(null);
  /** The prompt to re-send if the user retries. */
  const [lastPrompt, setLastPrompt] = useState<string | null>(null);
  /**
   * The server turn a retry should continue: the last turn was interrupted
   * (its stream ended without finishing — a dropped connection or a server
   * restart) or failed. Retrying it reuses the stored question instead of
   * asking it a second time.
   */
  const [retryTurn, setRetryTurn] = useState<{ turnId: string; interrupted: boolean } | null>(null);
  /** Shown beside the retry action when the server said the turn is still running. */
  const [retryNotice, setRetryNotice] = useState<string | null>(null);
  /** The thread whose reload failed after a refused retry: "Try again" reloads it. */
  const [reloadThreadId, setReloadThreadId] = useState<string | null>(null);
  const [inspectedBundle, setInspectedBundle] = useState<{ id: string; readOnly: boolean } | null>(null);
  // 'list' = thread switcher; 'chat' = active conversation. We default to
  // 'chat' on first open so users land on a usable composer; the user opens
  // 'list' explicitly via the "Threads" button.
  const [view, setView] = useState<'list' | 'chat'>('chat');
  const createThread = useCreateAiThread();
  const updateThread = useUpdateAiThread();
  const stream = useChatStream();
  const queryClient = useQueryClient();
  const writesEnabled = useFeatureFlag('ai.agents.writes.enabled');
  const threadsQuery = useAiThreads();
  const historyQuery = useThreadMessages(thread?.id ?? null);
  // Guard so we auto-submit initialPrompt at most once per open cycle.
  const submittedInitialPromptRef = useRef<string | null>(null);
  // Track which threads we've hydrated to avoid re-loading on every render.
  const hydratedThreadIdRef = useRef<string | null>(null);
  /** Set by the Stop button so the turn that lands is labelled as cut short. */
  const stoppedRef = useRef(false);

  /*
    Which conversation to come back to.

    Threads have always been stored server-side and the switcher has always
    listed them, but a reload dropped the ACTIVE one: `thread` starts null, so
    the panel reopened on an empty "New conversation" with the previous
    exchange one unlabelled glyph away. People read that as the history being
    gone (QA ledger ASK-003).

    Per browser, not per account, which is why it is localStorage and not the
    thread record: "where I was" is a property of this tab, not of the
    conversation. Wrapped because a browser with storage disabled must still
    open the panel — losing the restore is a smaller failure than not opening.
  */
  const rememberedThread = (): string | null => {
    try {
      return window.localStorage.getItem(LAST_THREAD_KEY);
    } catch {
      return null;
    }
  };
  const rememberThread = (threadId: string | null): void => {
    try {
      if (threadId === null) window.localStorage.removeItem(LAST_THREAD_KEY);
      else window.localStorage.setItem(LAST_THREAD_KEY, threadId);
    } catch {
      /* storage disabled — the panel still works, it just forgets. */
    }
  };
  /** Restored at most once per open, and never over an explicit thread. */
  const restoredRef = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset the initial-prompt guard whenever the sidebar closes so reopens
  // with a fresh prompt still auto-submit.
  useEffect(() => {
    if (!open) submittedInitialPromptRef.current = null;
  }, [open]);

  // When opening with a specific thread, select it as soon as thread data is available.
  const handledInitialThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) { handledInitialThreadRef.current = null; return; }
    if (!initialThreadId || handledInitialThreadRef.current === initialThreadId) return;
    const t = threadsQuery.data?.find((x) => x.id === initialThreadId);
    if (t) {
      setThread(t);
      setView('chat');
      handledInitialThreadRef.current = initialThreadId;
    }
  }, [open, initialThreadId, threadsQuery.data]);

  /*
    Reopen where the reader left off.

    Yields to every more specific intent: a thread named by the caller, a
    prompt to auto-submit, or a thread already chosen this cycle. It only ever
    selects a thread the server still lists, so a deleted or foreign id is
    forgotten rather than producing an empty panel with a stale header.
  */
  useEffect(() => {
    if (!open) { restoredRef.current = false; return; }
    if (restoredRef.current || thread || initialThreadId || initialPrompt) return;
    if (!threadsQuery.data) return;
    restoredRef.current = true;
    const rememberedId = rememberedThread();
    if (!rememberedId) return;
    const remembered = threadsQuery.data.find((t) => t.id === rememberedId);
    if (remembered) setThread(remembered);
    else rememberThread(null);
    // `thread` is read to bail out, not to re-run: adding it would re-enter the
    // moment the restore succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialThreadId, initialPrompt, threadsQuery.data]);

  // Whatever selected the active thread — restore, switcher, a new turn — this
  // is the one place that records it.
  useEffect(() => {
    if (thread) rememberThread(thread.id);
  }, [thread]);

  // When the active thread changes, hydrate `messages` from server history
  // exactly once per thread id. Streaming messages append on top of this.
  useEffect(() => {
    if (!thread) {
      // Guarded: an unconditional reset re-renders on every pass, and this
      // effect depends on a query result whose identity is only stable because
      // react-query makes it so. Cheap insurance against a render loop.
      setMessages((prev) => (prev.length === 0 ? prev : []));
      hydratedThreadIdRef.current = null;
      return;
    }
    if (hydratedThreadIdRef.current === thread.id) return;
    if (!historyQuery.data) return;
    const hydrated = historyQuery.data
      .map(toLocalMessage)
      .filter((m): m is LocalMessage => m !== null);
    setMessages(hydrated);
    hydratedThreadIdRef.current = thread.id;

    // Retroactively title threads that still have the default placeholder title.
    if (thread.title === 'New conversation' || !thread.title) {
      const firstUserMsg = historyQuery.data.find((m) => m.role === 'user');
      if (firstUserMsg) {
        updateThread.mutate({ id: thread.id, patch: { title: deriveTitle(firstUserMsg.content) } });
      }
    }
  }, [thread, historyQuery.data]);

  // Background sweep: fix any untitled threads whose messages are already cached.
  useEffect(() => {
    if (!open || !threadsQuery.data) return;
    const untitled = threadsQuery.data.filter(
      (t) => (t.title === 'New conversation' || !t.title) && t.messageCount > 0
    );
    for (const t of untitled) {
      const cached = queryClient.getQueryData<AiMessage[]>(aiQueryKeys.threadMessages(t.id));
      if (!cached) continue;
      const firstUserMsg = cached.find((m) => m.role === 'user');
      if (firstUserMsg) {
        updateThread.mutate({ id: t.id, patch: { title: deriveTitle(firstUserMsg.content) } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, threadsQuery.data]);

  const ensureThread = async (): Promise<AiThread> => {
    if (thread) return thread;
    const t = await createThread.mutateAsync({ title: 'New conversation' });
    let finalThread: AiThread = t;
    if (pageContext && !t.pinnedTo) {
      finalThread = await updateThread.mutateAsync({
        id: t.id,
        patch: {
          pinnedTo: {
            pluginId: pageContext.pluginId,
            entityType: pageContext.entityType,
            entityId: pageContext.entityId,
          },
        },
      });
    }
    setThread(finalThread);
    hydratedThreadIdRef.current = finalThread.id; // skip hydration — it's empty
    return finalThread;
  };

  const onSelectThread = (threadId: string) => {
    const t = threadsQuery.data?.find((x) => x.id === threadId);
    if (!t) return;
    setThread(t);
    setRetryTurn(null); // a turn retries only in its own conversation
    setRetryNotice(null);
    setView('chat');
  };

  const onNewThread = () => {
    setThread(null);
    setMessages([]);
    setRetryTurn(null);
    setRetryNotice(null);
    hydratedThreadIdRef.current = null;
    // Starting fresh is an instruction, not an accident: don't restore the old
    // one on the next open.
    rememberThread(null);
    setView('chat');
  };

  // Always reflect the latest title from the server even when local `thread` state is stale.
  const liveThread = thread ? (threadsQuery.data?.find((t) => t.id === thread.id) ?? thread) : null;

  // Resolve entity label for pinned threads so the header can show e.g. "Sales order SO-2026-00020".
  const pinnedEntityLabel = useEntityLabel({
    pluginId: liveThread?.pinnedTo?.pluginId,
    entityType: liveThread?.pinnedTo?.entityType,
    entityId: liveThread?.pinnedTo?.entityId,
  });

  const headerTitle: string | null = (() => {
    if (!liveThread) return null;
    if (liveThread.title && liveThread.title !== 'New conversation') return liveThread.title;
    if (liveThread.pinnedTo) {
      const entityTypeLabel = humanize(liveThread.pinnedTo.entityType);
      if (pinnedEntityLabel.label) return `${entityTypeLabel} ${pinnedEntityLabel.label}`;
      if (!pinnedEntityLabel.isPending) return entityTypeLabel;
    }
    return null;
  })();

  const sortedThreads = useMemo(() => {
    if (!threadsQuery.data) return [];
    return [...threadsQuery.data].sort((a, b) => {
      const at = new Date(a.lastMessageAt ?? a.updatedAt).getTime();
      const bt = new Date(b.lastMessageAt ?? b.updatedAt).getTime();
      return bt - at;
    });
  }, [threadsQuery.data]);

  /** Replace the local transcript with the server's (the answer of a resolved turn is there). */
  const reloadThreadMessages = async (threadId: string): Promise<void> => {
    setReloadThreadId(null);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: aiQueryKeys.threadMessages(threadId),
        queryFn: () => listAiMessages(threadId),
        staleTime: 0,
      });
      setMessages(fresh.map(toLocalMessage).filter((m): m is LocalMessage => m !== null));
      setTurnError(null);
    } catch {
      // "Try again" on this error reloads the thread again: the answer is on
      // the server, so sending the question would ask it a second time.
      setReloadThreadId(threadId);
      setTurnError('The conversation could not be reloaded. Reopen it to see the answer.');
    }
  };

  const handleSubmit = async (text: string, isRetry = false, retryOfTurnId?: string) => {
    const isNewThread = !thread;
    if (!isRetry) setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLastPrompt(text);
    // A retry keeps the turn it retries until the retry succeeds or is
    // resolved: a retry that fails before its own turn starts must still
    // retry that turn, never fall back to asking the question afresh.
    if (!retryOfTurnId) setRetryTurn(null);
    setRetryNotice(null);
    setReloadThreadId(null);
    stoppedRef.current = false;
    // A thread that cannot be created (no company to act for, a missing
    // permission) used to end the turn silently: the question sat there with
    // no answer and no error.
    let t: AiThread;
    try {
      t = await ensureThread();
    } catch (err) {
      setTurnError(err instanceof Error ? err.message : 'The conversation could not be started.');
      return;
    }
    const { finalText, finalTraces, persisted, error, interrupted, retryTurnId, retryRefused } = await stream.send({
      threadId: t.id,
      content: text,
      pageContext,
      ...(retryOfTurnId ? { retryOfTurnId } : {}),
    });
    const wasStopped = stoppedRef.current;
    stoppedRef.current = false;

    // A failed turn is not an answer. The stream can fail two ways: with an
    // explicit error event, or by returning the upstream service's own JSON
    // error payload AS the message body — which used to land in a normal
    // assistant bubble, indistinguishable from a real reply (QA ledger ASK-002).
    const rawErrorAsAnswer = finalText.trim() !== '' && looksLikeRawError(finalText);
    // A turn that produced nothing is also a failure. It used to append an
    // empty assistant bubble, which reads as the assistant having replied with
    // silence rather than as something having gone wrong.
    const emptyAnswer = finalText.trim() === '' && finalTraces.length === 0;
    /*
      A turn the reader stopped is not a turn that failed. Without this, asking
      the assistant to stop produced "That didn't work" with a Try again button
      — blaming the product for doing what it was told (QA ledger ASK-004). A
      stop that caught nothing at all simply leaves the thread as it was.
    */
    if (wasStopped) {
      setTurnError(null);
      setRetryTurn(null);
      if (finalText.trim() !== '') {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: finalText,
            toolTrace: finalTraces.map((tr) => ({ name: tr.name, uiLabel: tr.uiLabel, done: tr.done })),
            draftRefs: [],
            stopped: true,
          },
        ]);
      }
      return;
    }
    /*
      The server refused the retry (409). Never answered by sending the
      question again — that would be a second copy of it and a new turn that
      can repeat auto-applied writes. A turn whose replica died stays
      `streaming` until its lease expires and it is reaped (a few minutes):
      keep the retry and say so. A turn that completed, or that another retry
      took, has its answer in the thread: reload it and drop the retry.
    */
    if (retryRefused && retryOfTurnId) {
      setTurnError(null);
      if (retryRefused === 'TURN_STILL_RUNNING') {
        setRetryTurn({ turnId: retryOfTurnId, interrupted: true });
        setRetryNotice('The answer is still finishing on the server. Try again in a minute.');
        return;
      }
      setRetryTurn(null);
      await reloadThreadMessages(t.id);
      return;
    }
    if (retryTurnId) setRetryTurn({ turnId: retryTurnId, interrupted });
    // Interrupted is not "that didn't work": nothing went wrong with the
    // question, the answer just never arrived. Offer to continue it.
    if (interrupted) {
      setTurnError(null);
      return;
    }
    if (error || rawErrorAsAnswer || emptyAnswer) {
      setTurnError(error ?? (rawErrorAsAnswer ? finalText : 'The assistant returned no answer.'));
      return;
    }
    setTurnError(null);
    setRetryTurn(null);

    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: finalText,
        toolTrace: finalTraces.map((tr) => ({ name: tr.name, uiLabel: tr.uiLabel, done: tr.done })),
        draftRefs: [],
        messageId: persisted?.assistantMessageId,
      },
    ]);

    if (finalTraces.some((tr) => tr.name === 'ai.addNote')) {
      queryClient.invalidateQueries({ queryKey: [...aiQueryKeys.all, 'notes'] });
    }

    // Auto-title the thread from the first user message so it's never "Untitled chat".
    // Write directly into the cache so liveThread reflects the title immediately
    // without waiting for the background refetch triggered by invalidateQueries.
    if (isNewThread) {
      const updated = await updateThread.mutateAsync({ id: t.id, patch: { title: deriveTitle(text) } });
      setThread(updated);
      queryClient.setQueryData<AiThread[]>(aiQueryKeys.threads(), (old) =>
        old ? old.map((x) => (x.id === updated.id ? updated : x)) : [updated]
      );
    }

    // Bubble the active thread to the top of the list and refresh history
    // so re-opening this thread later shows the new turn from server truth.
    queryClient.invalidateQueries({ queryKey: aiQueryKeys.threads() });
    queryClient.invalidateQueries({ queryKey: aiQueryKeys.threadMessages(t.id) });
  };

  // Auto-submit the palette-supplied prompt on open. Guarded so React Strict
  // Mode double-invoke doesn't double-submit.
  useEffect(() => {
    if (!open) return;
    const prompt = initialPrompt?.trim();
    if (!prompt) return;
    if (submittedInitialPromptRef.current === prompt) return;
    submittedInitialPromptRef.current = prompt;
    void handleSubmit(prompt);
    // handleSubmit is stable within the component body and intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  const streamingLine: LocalMessage | null =
    stream.isStreaming && stream.currentText
      ? {
          role: 'assistant',
          content: stream.currentText,
          toolTrace: stream.toolEvents.map((t) => ({ name: t.name, uiLabel: t.uiLabel, done: t.done })),
          draftRefs: [],
        }
      : null;

  // Shown when we're streaming but nothing has been rendered yet — always
  // reflects a real backend action (current tool name) or "Thinking".
  const pendingLine: LocalMessage | null =
    stream.isStreaming && !stream.currentText
      ? {
          role: 'assistant',
          content: `_${derivePendingLabel(stream.toolEvents)}…_`,
          toolTrace: stream.toolEvents.map((t) => ({ name: t.name, uiLabel: t.uiLabel, done: t.done })),
        }
      : null;

  // After a page refresh stream.bundleIds is empty even when a draft bundle is
  // still pending server-side. Fall back to the latest still-active bundle the
  // server knows about for this thread so the DraftBundleCard re-mounts.
  const activeBundlesQuery = useAiBundlesForThread(thread?.id ?? null);
  // A turn whose proposal was refused leaves an empty bundle behind. Skip those:
  // the card to show is the newest one that holds a change, or the waiting
  // proposal (and its Discard) would never be reachable again.
  const activeBundles = activeBundlesQuery.data ?? [];
  const emptyBundleIds = new Set(
    activeBundles.filter((bundle) => bundle.itemCount === 0).map((bundle) => bundle.id)
  );
  const persistedActiveBundleId =
    activeBundles.find((bundle) => bundle.itemCount > 0)?.id ?? null;
  const currentBundleId =
    [...stream.bundleIds].reverse().find((id) => !emptyBundleIds.has(id)) ??
    persistedActiveBundleId;
  // The list above only learns a turn's bundle was left empty once it is read again.
  const refetchActiveBundles = activeBundlesQuery.refetch;
  useEffect(() => {
    if (!stream.isStreaming && thread?.id) void refetchActiveBundles?.();
  }, [stream.isStreaming, thread?.id, refetchActiveBundles]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes ai-gradient-sweep {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        data-testid="chat-scrim"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 14 }}
      />
      <aside
        role="complementary"
        aria-label="Ask LognAI"
        data-testid="chat-sidebar"
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 520,
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
          zIndex: 15,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              aria-label={view === 'list' ? 'Back to chat' : 'Show conversations'}
              onClick={() => setView(view === 'list' ? 'chat' : 'list')}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0, color: 'var(--muted-foreground)', lineHeight: 1 }}
              title={view === 'list' ? 'Back to chat' : 'Conversations'}
            >
              {/* The count is the affordance. A bare hamburger read as a
                  settings menu, and a reader who had just lost their place
                  concluded there was no history at all (QA ledger ASK-003).
                  "☰ 4" says there are four conversations behind it. */}
              {view === 'list' ? '←' : `☰${sortedThreads.length > 0 ? ` ${sortedThreads.length}` : ''}`}
            </button>
            {view === 'list' ? (
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--foreground)' }}>
                Conversations
              </span>
            ) : (
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Ask LognAI
              </span>
            )}
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted-foreground)', lineHeight: 1, padding: 4 }}
          >
            ✕
          </button>
        </header>
        {view === 'list' ? (
          <ThreadList
            threads={sortedThreads}
            activeThreadId={thread?.id ?? null}
            isLoading={threadsQuery.isPending}
            onSelect={onSelectThread}
            onNew={onNewThread}
          />
        ) : (
          <>
            {headerTitle && (
              <div style={{ padding: '10px 16px 10px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--foreground)' }}>
                  {headerTitle}
                </span>
              </div>
            )}
            <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
              <div style={{ height: '100%', padding: '12px 14px', overflow: 'auto', fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {thread && historyQuery.isPending && messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 12 }}>Loading history…</div>
                ) : null}
                {messages.map((m, i) => (
                  <ChatMessage
                    key={m.messageId ?? i}
                    role={m.role}
                    content={m.content}
                    toolTrace={m.toolTrace}
                    draftRefs={m.draftRefs}
                    threadId={thread?.id}
                    messageId={m.messageId}
                    stopped={m.stopped ?? false}
                  />
                ))}
                {streamingLine && <ChatMessage role={streamingLine.role} content={streamingLine.content} toolTrace={streamingLine.toolTrace} draftRefs={streamingLine.draftRefs} />}
                {pendingLine && <ChatMessage role={pendingLine.role} content={pendingLine.content} toolTrace={pendingLine.toolTrace} draftRefs={pendingLine.draftRefs} />}
                {/*
                  Ask the same question again.

                  Appends rather than replacing: the server has already stored
                  the first answer, and a control that hid it locally would make
                  the panel disagree with the transcript on the next reload.
                  Named for what it does — the thread shows the question twice
                  because it was asked twice (QA ledger ASK-004).
                */}
                {!stream.isStreaming &&
                  !turnError &&
                  lastPrompt &&
                  messages.length > 0 &&
                  messages[messages.length - 1]?.role === 'assistant' && (
                    <button
                      type="button"
                      onClick={() => void handleSubmit(lastPrompt, true)}
                      style={{
                        alignSelf: 'flex-start',
                        background: 'transparent',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        color: 'var(--muted-foreground)',
                        cursor: 'pointer',
                        fontSize: 11,
                        padding: '3px 8px',
                      }}
                    >
                      Ask again
                    </button>
                  )}
                {!stream.isStreaming && retryTurn?.interrupted && lastPrompt && (
                  <button
                    type="button"
                    onClick={() => void handleSubmit(lastPrompt, true, retryTurn.turnId)}
                    style={{
                      alignSelf: 'flex-start',
                      background: 'transparent',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--muted-foreground)',
                      cursor: 'pointer',
                      fontSize: 11,
                      padding: '3px 8px',
                    }}
                  >
                    Interrupted — retry
                  </button>
                )}
                {!stream.isStreaming && retryTurn?.interrupted && retryNotice && (
                  <div role="status" style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                    {retryNotice}
                  </div>
                )}
                {(turnError ?? stream.error) && (
                  <TurnError
                    detail={turnError ?? stream.error ?? ''}
                    isRetrying={stream.isStreaming}
                    onRetry={
                      reloadThreadId
                        ? () => void reloadThreadMessages(reloadThreadId)
                        : lastPrompt
                          ? () => void handleSubmit(lastPrompt, true, retryTurn?.turnId)
                          : undefined
                    }
                  />
                )}
                {currentBundleId && (
                  <DraftBundleCard
                    bundleId={currentBundleId}
                    onReview={(id) => setInspectedBundle({ id, readOnly: false })}
                    onViewHistory={(id) => setInspectedBundle({ id, readOnly: true })}
                  />
                )}
                {stream.undoBanners.map((b) => (
                  <UndoBanner
                    key={b.draftId}
                    draftId={b.draftId}
                    bundleId={b.bundleId}
                    deadlineAt={b.deadlineAt}
                    label={b.label}
                    onDismiss={() => stream.dismissUndoBanner(b.draftId)}
                  />
                ))}
              </div>
              {view === 'chat' && stream.isStreaming && (
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 120,
                    pointerEvents: 'none',
                    background: 'linear-gradient(90deg, rgba(37,99,235,0.2) 0%, rgba(37,99,235,0.2) 50%, rgba(37,99,235,0.2) 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'ai-gradient-sweep 2.5s ease infinite',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)',
                    maskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)',
                  }}
                />
              )}
            </div>
          </>
        )}
        {view === 'chat' && !writesEnabled && <KillSwitchBanner />}
        {view === 'chat' && (
          <ChatComposer
            onSubmit={handleSubmit}
            disabled={stream.isStreaming || (createThread.isPending && !thread)}
            isStreaming={stream.isStreaming}
            onStop={() => {
              stoppedRef.current = true;
              stream.cancel();
            }}
          />
        )}
      </aside>
      <BundleReviewPanel
        bundleId={inspectedBundle?.id ?? null}
        open={Boolean(inspectedBundle)}
        onClose={() => setInspectedBundle(null)}
        readOnly={inspectedBundle?.readOnly ?? false}
      />
    </>
  );
};
